import { Button, Group, Popover, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconFileTypeXls } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getExportErrorKey } from '@/module/Export/model/ExportApi';
import { useExportEntity } from '@/module/Export/model/ExportQuery';
import { DEFAULT_EXPORT_LANG, ExportEntityType, ValidationStatus } from '@/module/Export/model/ExportTypes';

import { NumberFilter } from '@/core/components';
import { CustomSelect } from '@/core/components/Select';

export interface ExportExcelButtonProps {
  entityType: ExportEntityType;
}

const MIN_YEAR = 1900;

const ALL_STATUSES = 'ALL';

export function ExportExcelButton({ entityType }: ExportExcelButtonProps) {
  const { t } = useTranslation();
  const [opened, { toggle, close }] = useDisclosure(false);

  const [status, setStatus] = useState<string>(ALL_STATUSES);
  const [year, setYear] = useState<string | number>('');

  const { mutate, isPending } = useExportEntity(entityType);

  const statusOptions = [
    { value: ALL_STATUSES, label: t('export.status.all') },
    { value: 'VALIDATED', label: t('export.status.validated') },
    { value: 'REJECTED', label: t('export.status.rejected') },
  ];

  const parsedYear = year !== '' && Number.isFinite(Number(year)) ? Number(year) : null;

  const handleExport = () =>
    mutate(
      {
        // the language picker is hidden for now, every export is generated in ro
        lang: DEFAULT_EXPORT_LANG,
        validationStatus: status === ALL_STATUSES ? null : (status as ValidationStatus),
        year: parsedYear,
      },
      {
        onSuccess: () => close(),
        onError: error =>
          notifications.show({
            color: 'red',
            title: t('notification.error.title'),
            message: t(getExportErrorKey(error)),
          }),
      },
    );

  return (
    <Popover
      opened={opened}
      onChange={close}
      position="bottom-end"
      withArrow
      trapFocus
      closeOnClickOutside={!isPending}
    >
      <Popover.Target>
        <Button
          size="xs"
          variant="outline"
          leftSection={<IconFileTypeXls size="20px" />}
          onClick={toggle}
        >
          {t('export.button')}
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack
          gap="sm"
          w={260}
        >
          <Text
            size="sm"
            fw="500"
          >
            {t('export.title')}
          </Text>
          <Text
            size="xs"
            c="dimmed"
          >
            {t('export.scopeHint')}
          </Text>
          <CustomSelect
            size="xs"
            label={t('export.status.label')}
            data={statusOptions}
            value={status}
            onChange={value => setStatus(value ?? ALL_STATUSES)}
            allowDeselect={false}
            // the option list is portalled by default, which the popover reads as a click outside
            comboboxProps={{ withinPortal: false }}
          />
          <NumberFilter
            label={t('export.year')}
            placeholder={t('export.allYears')}
            allowDecimal={false}
            min={MIN_YEAR}
            max={new Date().getFullYear()}
            value={year}
            onChange={setYear}
          />
          <Group justify="flex-end">
            <Button
              size="xs"
              loading={isPending}
              disabled={isPending}
              onClick={handleExport}
            >
              {t('export.download')}
            </Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
